import { useMeQuery } from '@shared/api';
import type { EconAnomaly, EconCalcFact, EconExtractedRow, EconRun, EconSource } from '@shared/api';

/**
 * Доменные хелперы раздела ОСНОВА.Экономист поверх типов из `@shared/api`.
 *
 * Типы сущностей (EconRun, EconAnomaly, …) живут в API-слайсе — здесь только
 * то, что фронт добавляет сверху: union'ы кодов модулей, маппинг статусов и
 * severity в тон Badge, человекочитаемые подписи и форматирование сумм/долей
 * по бухгалтерским стандартам. Дублировать эту логику в каждой странице
 * нельзя — она должна быть согласованной между списком, карточкой и трассой.
 */

/** Пять модулей управленческой отчётности (М1 БДР, М2 добыча, М3 столовая,
 *  М4 БДДС, М5 пакет руководству). Backend присылает `module: string`, но на
 *  фронте мы сужаем до известного union — это даёт исчерпывающие `switch` без
 *  default-веток с «сырым» кодом. `exec_pack` (М5) — мета-модуль: он собирается
 *  из утверждённых прогонов М1–М4 и не грузит документы (`docTypes: []`). */
export type EconModuleId = 'bdr' | 'mining' | 'canteen' | 'cashflow' | 'exec_pack';

export const ECON_MODULE_IDS: readonly EconModuleId[] = [
  'bdr',
  'mining',
  'canteen',
  'cashflow',
  'exec_pack',
] as const;

/** Тип-гард: пришёл ли из API код известного модуля. */
export function isEconModuleId(value: string): value is EconModuleId {
  return (ECON_MODULE_IDS as readonly string[]).includes(value);
}

/** Один тип входного документа для конкретного модуля. Имя поля файла в
 *  FormData = `docType` (контракт createEconRun из PR #140). Литералы
 *  `docType` — КАНОНИЧЕСКИЕ первичные doc_type движка econ; фронт обязан
 *  слать именно их, иначе документ молча игнорируется при классификации. */
/** Семантический id SVG-иконки типа документа. Маппинг id → компонент
 *  (`@shared/ui/icons`) живёт в UI-слое (`pages/Economist`) — entities
 *  остаются чистыми данными без React-компонентов. */
export type EconDocIcon =
  | 'act'
  | 'file-stack'
  | 'bar-chart'
  | 'clock'
  | 'truck'
  | 'target'
  | 'banknote'
  | 'users'
  | 'id-card'
  | 'pickaxe'
  | 'ruler'
  | 'package'
  | 'file-minus'
  | 'utensils'
  | 'clipboard-list'
  | 'calculator';

export interface EconDocType {
  /** Значение поля FormData (несёт doc_type на backend). Канонический литерал. */
  docType: string;
  /** SVG-иконка типа (id из набора `@shared/ui/icons`, см. [`EconDocIcon`]). */
  icon: EconDocIcon;
  /** Человекочитаемая подпись поля загрузки. */
  label: string;
  /** Подсказка под полем — что именно приложить. */
  hint: string;
  /** accept для input[type=file]. */
  accept: string;
  /** Содержит персональные данные (ФИО) — обезличивается на backend.
   *  Показываем пользователю пометку, чтобы снять вопрос «а это законно». */
  pii?: boolean;
  /** Допустима ли множественная загрузка этого типа в одной обработке.
   *  Для КС-2/КС-3 backend обрабатывает ВСЕ загруженные файлы (несколько
   *  актов/книг за период — норма), поэтому предупреждать о «дубле» нельзя.
   *  По умолчанию (`undefined`) тип считается одиночным — несколько файлов
   *  одного типа подсвечиваются как вероятная ошибка выбора. */
  multiple?: boolean;
  /** Регэкспы по имени файла для авто-определения типа при пакетной загрузке.
   *  Срабатывают по `file.name` (без учёта регистра). Нужны, чтобы не раздавать
   *  «мусорный» дефолт по позиции файла — лучше угадать осмысленно или оставить
   *  выбор пустым, чем подставить заведомо неверный тип. */
  filenameHints?: readonly RegExp[];
}

/** Описание модуля: заголовок, аннотация и список doc_type,
 *  которые загружаются для обработки этого модуля. */
export interface EconModuleConfig {
  id: EconModuleId;
  title: string;
  short: string;
  description: string;
  /** Метрика, выносимая на карточку-сводку (для дашборда / счётчиков). */
  headlineMetric: string;
  docTypes: EconDocType[];
}

/** Первичка приходит в разных форматах: таблицы (1С-выгрузки, реестры),
 *  сканы актов/накладных (PDF), отчёты (DOCX). Держим accept широким, но
 *  осмысленным по характеру документа. */
const TABLES = '.xlsx,.xls,.csv';
const TABLES_PDF = '.xlsx,.xls,.csv,.pdf';
const DOCS = '.xlsx,.xls,.csv,.pdf,.docx';

export const ECON_MODULES: Record<EconModuleId, EconModuleConfig> = {
  bdr: {
    id: 'bdr',
    title: 'Бюджет доходов и расходов',
    short: 'БДР',
    description:
      'Свод доходов и расходов по объекту из первички: акты КС-2/КС-3 формируют доход, выгрузка из 1С — затраты, табели и путевые листы раскрывают ФОТ и работу техники. Агент сверяет факт с планом по объекту и подсвечивает отклонения.',
    headlineMetric: 'Отклонение от плана',
    docTypes: [
      {
        docType: 'act_ks2',
        icon: 'act',
        label: 'Акт КС-2',
        hint: 'Акт о приёмке выполненных работ — формирует доход. Скан или выгрузка.',
        accept: DOCS,
        // За период обычно несколько КС-2 (по разным договорам/объектам) —
        // backend обрабатывает все. Дубль типа здесь не ошибка.
        multiple: true,
        // «КС-2», «КС 2», «КС2» в имени файла → этот тип. `Выполнение`/`КС-3`
        // намеренно НЕ ловим тут — они уходят в summary_ks3.
        filenameHints: [/кс[\s_-]*2(?![\d])/i, /\bks[\s_-]*2\b/i],
      },
      {
        docType: 'summary_ks3',
        icon: 'file-stack',
        label: 'Справка КС-3',
        hint: 'Справка о стоимости выполненных работ и затрат — формирует доход.',
        accept: DOCS,
        // Книг КС-3 за период также может быть несколько (по договорам).
        multiple: true,
        // «КС-3», «КС 3», «КС3», а также книга «Выполнение» (так названы
        // листы/файлы книги КС-3 в реальных выгрузках) → этот тип.
        filenameHints: [/кс[\s_-]*3(?![\d])/i, /\bks[\s_-]*3\b/i, /выполнени/i],
      },
      {
        docType: 'ledger',
        icon: 'bar-chart',
        label: 'Выгрузка затрат из 1С',
        hint: 'ОСВ или реестр проводок по объекту — источник фактических затрат.',
        accept: TABLES,
      },
      {
        docType: 'timesheet',
        icon: 'clock',
        label: 'Табель учёта рабочего времени',
        hint: 'Отработанные часы по сотрудникам — раскрывает ФОТ.',
        accept: TABLES_PDF,
        pii: true,
      },
      {
        docType: 'waybill',
        icon: 'truck',
        label: 'Путевые листы',
        hint: 'Машино-часы и пробег техники — работа спецтехники и транспорта.',
        accept: DOCS,
      },
      {
        docType: 'object_plan',
        icon: 'target',
        label: 'План по объекту',
        hint: 'Плановая карточка / бюджет объекта — база для сверки план→факт.',
        accept: TABLES_PDF,
      },
      {
        docType: 'payroll',
        icon: 'banknote',
        label: 'Платёжная ведомость',
        hint: 'Начисления ФОТ по сотрудникам — детализация затрат на оплату труда.',
        accept: TABLES_PDF,
        pii: true,
      },
      {
        docType: 'personnel_register',
        icon: 'users',
        label: 'Кадровый реестр',
        hint: 'Численность сотрудников по объекту/подразделению.',
        accept: TABLES_PDF,
        pii: true,
      },
    ],
  },
  mining: {
    id: 'mining',
    title: 'Добыча полезных ископаемых',
    short: 'Добыча ПИ',
    description:
      'Контроль добычи в пределах лицензионных лимитов и сверка объёмов: журнал добычи, маркшейдерский замер и реестр отгрузок против плана и годовых лимитов из реестра лицензий. Агент ловит расхождения и риск превышения недропользования.',
    headlineMetric: 'Объём добычи к лимиту',
    docTypes: [
      {
        docType: 'mining_license_registry',
        icon: 'id-card',
        label: 'Реестр лицензий на недропользование',
        hint: 'Годовые лимиты добычи по лицензиям — потолок для сверки.',
        accept: TABLES_PDF,
      },
      {
        docType: 'extraction_log',
        icon: 'pickaxe',
        label: 'Журнал добычи',
        hint: 'Фактические объёмы добычи за период.',
        accept: TABLES_PDF,
      },
      {
        docType: 'shipment_registry',
        icon: 'truck',
        label: 'Реестр отгрузок / УПД / ТОРГ-12',
        hint: 'Отгрузки и реализация: УПД, ТОРГ-12, реестр отгрузок.',
        accept: DOCS,
      },
      {
        docType: 'survey',
        icon: 'ruler',
        label: 'Маркшейдерский замер',
        hint: 'Контрольный маркшейдерский замер остатков и выработки.',
        accept: DOCS,
      },
      {
        docType: 'mining_plan',
        icon: 'target',
        label: 'План добычи',
        hint: 'Производственная программа — плановые объёмы за период.',
        accept: TABLES_PDF,
      },
    ],
  },
  canteen: {
    id: 'canteen',
    title: 'Столовая',
    short: 'Столовая',
    description:
      'Контроль продуктового баланса и наценки: приход по накладным, списания, инвентаризация и калькуляционные карты против реестра обслуживания и бюджета. Агент находит пересортицу, перерасход и отклонения по марже.',
    headlineMetric: 'Маржа реализации',
    docTypes: [
      {
        docType: 'food_invoice',
        icon: 'package',
        label: 'Приходные накладные на продукты',
        hint: 'Поступление продуктов от поставщиков — приходная часть.',
        accept: DOCS,
      },
      {
        docType: 'writeoff_act',
        icon: 'file-minus',
        label: 'Акты списания',
        hint: 'Списание продуктов в производство и порчу — расходная часть.',
        accept: DOCS,
      },
      {
        docType: 'canteen_register',
        icon: 'utensils',
        label: 'Реестр обслуживания (питания)',
        hint: 'Кто и сколько питался за период — база реализации.',
        accept: TABLES_PDF,
        pii: true,
      },
      {
        docType: 'inventory',
        icon: 'clipboard-list',
        label: 'Инвентаризационная ведомость',
        hint: 'Фактические остатки продуктов на дату — сверка баланса.',
        accept: TABLES_PDF,
      },
      {
        docType: 'calc_card',
        icon: 'calculator',
        label: 'Калькуляционные карты блюд',
        hint: 'Нормы закладки и себестоимость блюд — основа калькуляции.',
        accept: DOCS,
      },
      {
        docType: 'canteen_plan',
        icon: 'target',
        label: 'План / бюджет столовой',
        hint: 'Плановые показатели реализации и маржи — база для сверки.',
        accept: TABLES_PDF,
      },
      {
        docType: 'canteen_payroll',
        icon: 'bar-chart',
        label: 'ФОТ столовой',
        hint: 'Итоговая сумма ФОТ подразделения столовой за месяц — для субсидии с ФОТ.',
        accept: TABLES_PDF,
      },
    ],
  },
  cashflow: {
    id: 'cashflow',
    title: 'БДДС и платёжный календарь',
    short: 'БДДС',
    description:
      'Движение денежных средств по статьям из банковских выписок и скользящий платёжный календарь: прогноз остатков по неделям с детектом кассовых разрывов. Агент классифицирует операции по статьям ДДС, сверяет балансы счетов и предупреждает о нехватке средств.',
    headlineMetric: 'Прогноз кассовых разрывов',
    docTypes: [
      {
        docType: 'bank_statement',
        icon: 'bar-chart',
        label: 'Банковская выписка',
        hint: 'Выгрузка операций по счетам из 1С/банка (1CClientBankExchange .txt, XLSX, CSV, PDF) — источник фактического ДДС.',
        accept: '.txt,.xlsx,.xls,.csv,.pdf',
        multiple: true,
      },
      {
        docType: 'planned_payments',
        icon: 'file-minus',
        label: 'Реестр планируемых платежей',
        hint: 'Обязательные платежи по договорам со сроками — расходная часть календаря.',
        accept: TABLES_PDF,
      },
      {
        docType: 'expected_receipts',
        icon: 'target',
        label: 'График ожидаемых поступлений',
        hint: 'Ожидаемые поступления по договорам — приходная часть календаря.',
        accept: TABLES_PDF,
      },
    ],
  },
  exec_pack: {
    id: 'exec_pack',
    title: 'Пакет руководству',
    short: 'Пакет',
    description:
      'Консолидированный пакет KPI по всем направлениям за период: сводка для руководства с проверкой согласованности цифр (принцип «одна цифра») и пояснительной запиской. Собирается автоматически из уже утверждённых расчётов М1–М4 — документы прикладывать не нужно.',
    headlineMetric: 'Согласованность цифр по направлениям',
    // Пакет не грузит первичку — источник данных — финализированные прогоны
    // М1–М4. Пустой `docTypes` включает в форме прогона режим «без файлов».
    docTypes: [],
  },
};

export type EconStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type EconDecision = 'accepted' | 'rejected' | 'disputed';
export type EconSeverity = 'critical' | 'major' | 'minor';
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Статус обработки активный (имеет смысл polling)? */
export function isRunActive(status: string): boolean {
  return status === 'pending' || status === 'processing';
}

export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готово';
    case 'processing':
      return 'расчёт…';
    case 'pending':
      return 'в очереди';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

/**
 * Подпись текущего этапа обработки для полосы прогресса (по-русски). Backend в
 * событии `econ_run_updated` присылает только `status` + `progress` (без
 * stage-кода), поэтому этап выводим эвристически из процента: грубые рубежи
 * пайплайна econ (извлечение строк → расчёт показателей → правила аномалий →
 * сборка). Это подпись «что сейчас происходит», не точный stage — точного в
 * контракте нет. Для терминальных статусов вызывающий показывает свой текст.
 */
export function runStageLabel(status: string, progressPercent: number): string {
  if (status === 'pending') return 'В очереди на обработку';
  if (status === 'failed') return 'Обработка прервана';
  if (status === 'completed') return 'Обработка завершена';
  // processing — раскладываем по рубежам прогресса.
  const p = Math.max(0, Math.min(100, progressPercent));
  if (p < 5) return 'Подготовка документов';
  if (p < 45) return 'Извлечение строк из документов';
  if (p < 70) return 'Расчёт показателей';
  if (p < 90) return 'Поиск аномалий';
  return 'Завершение расчёта';
}

export function severityTone(severity: string): BadgeTone {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'info';
    default:
      return 'neutral';
  }
}

export function severityLabel(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'критический';
    case 'major':
      return 'важный';
    case 'minor':
      return 'незначительный';
    default:
      return severity;
  }
}

/**
 * Число критических аномалий БЕЗ разбора — регуляторный гейт утверждения
 * обработки. Зеркалит backend `econ::unresolved_critical_count`
 * (osnova-agents/src/econ/mod.rs): критическая считается «разобранной» только
 * при решении `rejected` (экономист явно отклонил/устранил). При `> 0`
 * утверждение обработки и сборка отчёта запрещены (backend вернёт 409) — на
 * фронте этим же счётчиком гасим кнопку «Утвердить», чтобы пользователь видел
 * причину ДО клика, а не ловил ошибку после.
 */
export function unresolvedCriticalCount(anomalies: readonly EconAnomaly[]): number {
  return anomalies.filter((a) => a.severity === 'critical' && a.decision !== 'rejected').length;
}

export function decisionTone(decision: string): BadgeTone {
  switch (decision) {
    case 'accepted':
      return 'success';
    case 'rejected':
      return 'neutral';
    case 'disputed':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function decisionLabel(decision: string): string {
  switch (decision) {
    case 'accepted':
      return 'принято';
    case 'rejected':
      return 'отклонено';
    case 'disputed':
      return 'спорно';
    default:
      return decision;
  }
}

/* ───────────────────────────────────────────────────────────────────────────
 * Русские подписи внутренних кодов движка econ (ТЗ п.5.6: язык интерфейса и
 * всех сообщений агента — русский). Пользователь не должен видеть сырые коды
 * `revenue_ks2` / `reestr_total_mismatch` / `cost_line` и т.п.
 *
 * Источники кодов (backend, osnova-agents/src/econ):
 *  - метрики          — calculator/{bdr,mining,canteen}.rs (поле `metric`);
 *  - kind аномалий    — там же (поле `kind` аномалии);
 *  - kind строк       — prompts/{bdr,mining,canteen}.rs (`row_kind`/`kinds`/KIND_*);
 *  - ключи payload    — prompts/* (контракт JSON извлекаемой строки).
 *
 * Каждая мапа неполна по дизайну: НЕИЗВЕСТНЫЙ код возвращается как есть (через
 * `labelOr`), чтобы при добавлении кода на backend интерфейс не падал, а лишь
 * показывал технический код до обновления словаря. Базовые коски покрыты.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Вернуть подпись из мапы, иначе сам код (graceful fallback на новые коды). */
function labelOr(map: Readonly<Record<string, string>>, code: string): string {
  return map[code] ?? code;
}

/** Подписи расчётных метрик (поле `metric` факта). Терминология — из шаблона
 *  «Приложение № 1» заказчика (БДР) и доменных формулировок добычи/столовой. */
const METRIC_LABELS: Readonly<Record<string, string>> = {
  // --- БДР (calculator/bdr.rs) ---
  // «КС-3 (всего)», а не «СМР по КС-3»: 111,3 млн — это грузовой «Всего работ
  // и затрат» книги КС-3 (СМР + косвенные + материалы), а не чистый СМР
  // (74,07 млн). Слово «СМР» здесь вводит не-экономиста в заблуждение.
  revenue_ks3: 'Выполнено по КС-3 (всего)',
  revenue_ks3_cumulative: 'Выполнено по КС-3 (всего, накопительно)',
  revenue_ks2: 'Выполнено по КС-2 (НЗП, справочно)',
  revenue_total: 'Доход всего',
  contractor_materials: 'в т.ч. материалы Подрядчика',
  genpodryad_services: 'Услуги генподряда (7,5%)',
  customer_materials_total: 'Материалы Заказчика (справочно, не в итоге)',
  customer_equipment_total: 'Оборудование Заказчика (справочно, не в итоге)',
  cost_by_article: 'Затраты по статье',
  cost_total: 'Затраты всего',
  margin: 'Маржа',
  man_hours: 'Человеко-часы',
  machine_hours: 'Машино-часы',
  // Управленческая надстройка БДР: распределение АУП (Р-07) и прибыли (Р-08).
  aup_pool: 'АУП (пул к распределению)',
  aup_distributed: 'АУП (распределено по объектам)',
  profit_total: 'Прибыль к распределению',
  profit_distributed: 'Прибыль (распределено по объектам)',
  // Сверка материалов (Д14): поставки МТО в затраты не входят.
  materials_supply: 'Поставки МТО (справочно, не в затратах)',
  // --- Добыча (calculator/mining.rs) ---
  extraction_total: 'Добыто всего',
  extraction_cumulative: 'Добыто нарастающим итогом (с начала года)',
  shipment_total: 'Отгружено всего',
  // Объёмы приведены к базовым м³ (Р-05); суффикс единицы у объёмов не
  // показываем — как у прочих объёмных метрик добычи.
  external_sales: 'Внешняя реализация (объём)',
  internal_release: 'Внутренний отпуск (объём)',
  extraction_loss: 'Нормативные потери',
  reserves_balance: 'Остаток запасов на конец',
  license_balance: 'Остаток лимита по лицензии',
  // --- Столовая (calculator/canteen.rs) ---
  cost_per_portion: 'Себестоимость порции',
  avg_check: 'Средний чек',
  employer_subsidy: 'Дотация работодателя',
  employer_subsidy_with_fot: 'Дотация работодателя (с ФОТ)',
  supply_total: 'Поступление продуктов всего',
  supply_by_category: 'Поступление по категории',
  writeoff_total: 'Списано всего',
  served_count: 'Обслужено (порций)',
  revenue_by_method: 'Выручка по способу оплаты',
  // `revenue_total` уже определён выше — общий код БДР/добычи/столовой.
};

/** Человекочитаемое название метрики (`null` неизвестен → сам код). */
export function labelMetric(metric: string): string {
  return labelOr(METRIC_LABELS, metric);
}

/** Подписи видов аномалий (поле `kind` аномалии). */
const ANOMALY_KIND_LABELS: Readonly<Record<string, string>> = {
  // --- БДР ---
  ks2_total_mismatch: 'Итог КС-2 не сходится с суммой строк',
  reestr_total_mismatch: 'Итог реестра не сходится со слагаемыми',
  cost_article_unmapped: 'Затраты без статьи',
  negative_margin: 'Отрицательная маржа',
  contract_revenue_mismatch: 'КС-3 (накопл.) не сходится с реестром «Всего»',
  ks3_period_reestr_mismatch: 'КС-3 (период) не сходится с реестром (текущий акт)',
  ks3_grand_total_mismatch: 'Σ объектов не сходится с «Всего работ и затрат»',
  ks2_without_ks3: 'КС-2 без/сверх КС-3 (проверьте методику)',
  contract_overrun: 'Освоение превысило цену договора',
  ks2_vs_ks3_gap: 'Расхождение КС-2 и КС-3',
  ks_source_unrecognized: 'Источник КС не распознан',
  revenue_contract_unmapped: 'Договор без привязки к шаблону (в отчёте будет 0)',
  // Раскладка R4 (единичные расценки): аномалии калькулятора breakdown.
  unit_rate_not_found: 'Нет единичной расценки по коду позиции',
  breakdown_reconcile_mismatch: 'Раскладка превышает стоимость КС-2',
  // --- Добыча ---
  license_limit_exceeded: 'Превышен лимит лицензии',
  license_limit_approaching: 'Приближение к лимиту лицензии',
  survey_mismatch: 'Расхождение с маркшейдерским замером',
  survey_missing: 'Нет маркшейдерского замера',
  // --- Столовая / общие ---
  stock_balance_mismatch: 'Расхождение остатков по балансу',
  plan_fact_gap: 'Отклонение факта от плана',
  unit_mismatch: 'Несовпадение единиц измерения',
  // --- Качество источника (предобработка / БДДС) ---
  source_too_large: 'Документ слишком велик для выбранного типа',
  ledger_format_suspect: 'Файл не похож на выгрузку 1С',
  statement_balance_mismatch: 'Не сходится баланс выписки 1С',
  // --- Объекты и материалы БДР (Э0б: Д3, Д14) ---
  object_unmapped: 'Объект не найден в справочнике объектов',
  object_scope_unmapped: 'Объект прогона не найден в справочнике',
  materials_without_m29: 'Поставки МТО без списания М-29',
  m29_undated: 'Строки М-29 без даты не учтены',
  auto_collect_failed: 'Данные из системы не прочитаны',
};

/** Человекочитаемое название вида аномалии (неизвестный → сам код). */
export function labelAnomalyKind(kind: string): string {
  return labelOr(ANOMALY_KIND_LABELS, kind);
}

/** Подписи видов извлечённых строк (поле `kind` строки). */
const ROW_KIND_LABELS: Readonly<Record<string, string>> = {
  // --- БДР ---
  revenue_line: 'Строка дохода (КС-2/КС-3)',
  cost_line: 'Строка затрат (1С)',
  headcount_fact: 'Факт по труду (табель)',
  machine_hours_fact: 'Факт по технике (путевой лист)',
  plan_line: 'Плановая строка',
  // --- Добыча ---
  mining_license: 'Лицензия',
  extraction_entry: 'Запись добычи',
  shipment_entry: 'Запись отгрузки',
  survey_measurement: 'Маркшейдерский замер',
  license_plan: 'План по лицензии',
  // --- Столовая ---
  supply_line: 'Поступление продуктов',
  writeoff_line: 'Списание',
  service_line: 'Обслуживание (питание)',
  inventory_line: 'Инвентаризация',
  calc_card_line: 'Калькуляционная карта',
  canteen_plan_line: 'Плановая строка (столовая)',
};

/** Человекочитаемое название вида строки (неизвестный → сам код). */
export function labelRowKind(kind: string): string {
  return labelOr(ROW_KIND_LABELS, kind);
}

/** Подписи ключей payload извлечённых строк (контракт JSON из prompts/*). */
const PAYLOAD_KEY_LABELS: Readonly<Record<string, string>> = {
  // --- Идентификация / общие ---
  contract: 'Договор',
  source: 'Источник',
  object: 'Объект',
  period: 'Период',
  date: 'Дата',
  doc_date: 'Дата документа',
  position: 'Позиция',
  name: 'Наименование',
  product: 'Продукт',
  dish: 'Блюдо',
  article: 'Статья',
  cost_article: 'Статья затрат',
  raw_article: 'Статья (как в документе)',
  cost_article_rule: 'Статья по правилу справочника',
  cost_article_llm: 'Статья по распознаванию',
  account: 'Счёт 1С',
  object_raw: 'Объект (как в документе)',
  object_unmapped: 'Объект не найден в справочнике',
  project_id: 'Объект строительства (ид.)',
  material_basis: 'Основание (материалы)',
  basis: 'Основание',
  delivery_date: 'Дата поставки',
  category: 'Категория',
  metric: 'Показатель',
  source_ref: 'Ссылка на источник',
  confidence: 'Уверенность',
  // --- Объёмы / суммы ---
  unit: 'Ед. изм.',
  quantity: 'Количество',
  qty: 'Количество',
  volume: 'Объём',
  amount: 'Сумма',
  total: 'Итого',
  unit_price: 'Цена за единицу',
  unit_cost: 'Себестоимость единицы',
  paid_amount: 'Оплачено',
  plan_value: 'План',
  // --- Реестр КС-2 (книга КС-3) ---
  direct: 'Прямые затраты',
  indirect: 'Косвенные затраты',
  contractor_materials: 'Материалы Подрядчика',
  customer_materials: 'Материалы Заказчика',
  customer_equipment: 'Оборудование Заказчика',
  indexation: 'Индексация',
  reestr_total: 'Итого по реестру',
  // --- Труд / техника ---
  employee: 'Сотрудник',
  man_hours: 'Человеко-часы',
  machine: 'Техника',
  machine_hours: 'Машино-часы',
  headcount: 'Численность',
  // --- Добыча ---
  license_no: 'Номер лицензии',
  mineral: 'Полезное ископаемое',
  annual_limit: 'Годовой лимит',
  remaining: 'Остаток лимита',
  measured_volume: 'Замеренный объём',
  extraction_plan: 'План добычи',
  shipment_plan: 'План отгрузки',
  buyer: 'Покупатель',
  counterparty: 'Контрагент',
  license_no_inferred: 'Лицензия (определена косвенно)',
  license_inferred: 'Лицензия определена косвенно',
  // --- Столовая ---
  supplier: 'Поставщик',
  payment_method: 'Способ оплаты',
  method: 'Способ оплаты',
  services_count: 'Кол-во обслуживаний',
  plan_services_count: 'План обслуживаний',
  actual_qty: 'Фактический остаток',
  book_qty: 'Учётный остаток',
  norm_per_portion: 'Норма на порцию',
  portion_output: 'Выход порции',
  plan_cost_per_portion: 'План себестоимости порции',
  prev_cost_per_portion: 'Прошлая себестоимость порции',
  plan_revenue: 'План выручки',
  revenue_plan: 'План выручки',
};

/** Человекочитаемая подпись ключа payload (неизвестный → сам ключ). */
export function labelPayloadKey(key: string): string {
  return labelOr(PAYLOAD_KEY_LABELS, key);
}

/** Подписи типов входных документов (`doc_type` источника). Переиспользует
 *  `label` из [`ECON_MODULES`]; добавляет алиасы doc_type, которые backend
 *  распознаёт сверх канонических (см. mining `from_doc_type`). */
const DOC_TYPE_ALIASES: Readonly<Record<string, string>> = {
  auto_collect: 'Автосбор из системы (Сметчик / ПТО)',
  // Алиасы добычи (mining.rs from_doc_type), которых нет в ECON_MODULES.
  license_registry: 'Реестр лицензий на недропользование',
  licenses: 'Реестр лицензий на недропользование',
  mining_log: 'Журнал добычи',
  extraction_journal: 'Журнал добычи',
  shipments: 'Реестр отгрузок',
  sales_registry: 'Реестр отгрузок',
  upd: 'УПД',
  torg12: 'ТОРГ-12',
  surveyor_report: 'Маркшейдерский замер',
  mine_survey: 'Маркшейдерский замер',
  license_plan: 'План добычи',
  extraction_plan: 'План добычи',
  production_plan: 'План добычи',
};

/** Карта `doc_type` → подпись, собранная один раз из всех модулей. */
const DOC_TYPE_LABELS: Readonly<Record<string, string>> = (() => {
  const map: Record<string, string> = { ...DOC_TYPE_ALIASES };
  for (const id of ECON_MODULE_IDS) {
    for (const dt of ECON_MODULES[id].docTypes) {
      map[dt.docType] = dt.label;
    }
  }
  return map;
})();

/** Человекочитаемая подпись типа документа (неизвестный → сам код). */
export function labelDocType(docType: string): string {
  return labelOr(DOC_TYPE_LABELS, docType);
}

/* ───────────────────────────────────────────────────────────────────────────
 * Группировка расчётных фактов по договору (ТЗ п.3.3: результат — показатели,
 * сгруппированные по договору из `dimension.contract`).
 * ──────────────────────────────────────────────────────────────────────────── */

/** Прочитать строковое значение ключа `dimension` факта. `dimension: unknown`
 *  (kind-зависимый JSON) — читаем мягко, нестрока → `null`. */
export function dimensionField(dimension: unknown, key: string): string | null {
  if (typeof dimension !== 'object' || dimension === null) return null;
  const v = (dimension as Record<string, unknown>)[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** Короткие подписи разреза (строчные, для подписи `«разрез: значение»` под
 *  показателем). Для ключей вне карты падаем на [`labelPayloadKey`] со
 *  строчной первой буквой — новые разрезы движка не ломают вывод. */
const DIMENSION_SHORT_LABELS: Readonly<Record<string, string>> = {
  article: 'статья',
  cost_article: 'статья',
  raw_article: 'статья',
  object: 'объект',
  license_no: 'лицензия',
  basis: 'основание',
};

/** Подпись разреза одного ключа dimension (строчная): известные коды — из
 *  [`DIMENSION_SHORT_LABELS`], прочие — через [`labelPayloadKey`] со строчной
 *  первой буквой (`Способ оплаты` → `способ оплаты`). */
function dimensionKeyLabel(key: string): string {
  const short = DIMENSION_SHORT_LABELS[key];
  if (short) return short;
  const label = labelPayloadKey(key);
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** Один сегмент разреза: подпись + значение (`{ label: 'статья', value: '…' }`). */
export interface DimensionSegment {
  key: string;
  label: string;
  value: string;
}

/**
 * Разрез факта из `dimension` — все осмысленные строковые поля, КРОМЕ `contract`
 * (он уже заголовок группы) и ключей из `omit` (например `object`, если он
 * вынесен в подзаголовок группы). Нужен, чтобы десятки фактов одной метрики
 * («Затраты по статье», «Маржа») не сливались в одинаковые строки без признака
 * различия — у каждого показывается его разрез (статья / объект / лицензия /
 * способ оплаты). Порядок сегментов стабилен по приоритету осей; неизвестные
 * ключи идут в конце в порядке появления. Пустой dimension → `[]`.
 */
export function factDimensionSegments(
  dimension: unknown,
  omit: readonly string[] = [],
): DimensionSegment[] {
  if (typeof dimension !== 'object' || dimension === null) return [];
  const obj = dimension as Record<string, unknown>;
  // № договора больше НЕ скрываем безусловно: при группировке по объекту это
  // значимый вторичный разрез дохода (КС-2/КС-3). Если договор уже вынесен в
  // заголовок группы — его передают в `omit`.
  const skip = new Set<string>(omit);
  // Приоритет осей разреза: сначала № договора (доход по КС), затем статья/
  // объект/лицензия, способ оплаты/категория, далее остальное в порядке ключей.
  const PRIORITY = ['contract', 'article', 'cost_article', 'raw_article', 'object', 'license_no', 'method', 'payment_method', 'category'];
  // Технические ключи (`project_id` и прочие `*_id`) — для связи фактов с
  // объектом, а не для показа: id в разрезе пользователю ничего не говорит.
  const keys = Object.keys(obj).filter((k) => !skip.has(k) && !k.endsWith('_id'));
  keys.sort((a, b) => {
    const ia = PRIORITY.indexOf(a);
    const ib = PRIORITY.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const segments: DimensionSegment[] = [];
  for (const key of keys) {
    const value = obj[key];
    if (typeof value !== 'string' || value.trim() === '') continue;
    segments.push({ key, label: dimensionKeyLabel(key), value: value.trim() });
  }
  return segments;
}

/** Метрики, измеряемые в деньгах (рендерятся через [`fmtRub`]). Объёмы и часы
 *  (`man_hours`, `machine_hours`, `extraction_total`, `served_count` …) —
 *  через [`fmtNumber`] со своей размерностью. */
const MONEY_METRICS: ReadonlySet<string> = new Set([
  'revenue_ks3',
  'revenue_ks3_cumulative',
  'revenue_ks2',
  'revenue_total',
  'contractor_materials',
  'genpodryad_services',
  'customer_materials_total',
  'customer_equipment_total',
  'cost_by_article',
  'cost_total',
  'margin',
  'cost_per_portion',
  'avg_check',
  'employer_subsidy',
  'supply_total',
  'supply_by_category',
  'writeoff_total',
  // БДР: управленческая надстройка (пул/распределение АУП и прибыли) — рубли.
  'aup_pool',
  'aup_distributed',
  'profit_total',
  'profit_distributed',
  'materials_supply',
  // Столовая: субсидия с ФОТ (Р-10) и выручка по способу оплаты — рубли.
  'employer_subsidy_with_fot',
  'revenue_by_method',
]);

/** Денежная ли это метрика (для выбора формата суммы). */
export function isMoneyMetric(metric: string): boolean {
  return MONEY_METRICS.has(metric);
}

/** Единица измерения немонетарной метрики для подписи значения (часы, порции).
 *  Пустая строка — без явной единицы (объёмы добычи — т/м³ зависят от данных). */
export function metricUnit(metric: string): string {
  switch (metric) {
    case 'man_hours':
      return 'чел.-ч';
    case 'machine_hours':
      return 'маш.-ч';
    case 'served_count':
      return 'порц.';
    default:
      return '';
  }
}

/** Денежная сумма в рублях без копеек (управленческая отчётность — крупные
 *  числа, дробная часть шумит). `null` → прочерк. */
export function fmtMoney(value: number | null | undefined): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

/** Денежная сумма с копейками и знаком рубля (формат показателей блока
 *  «Результат», ТЗ п.3.3). Валютный форматтер `ru-RU`/`RUB`: тысячи разделены
 *  неразрывными пробелами, две цифры дробной части, знак рубля справа.
 *  `null`/NaN → прочерк. */
export function fmtRub(value: number | null | undefined): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Произвольное число с заданной точностью (объёмы, тонны, штуки). */
export function fmtNumber(value: number | null | undefined, digits = 2): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString('ru-RU', { maximumFractionDigits: digits });
}

/** Доля в процентах со знаком (отклонение). `+12.4 %` / `−3.0 %`. Минус —
 *  типографский U+2212, чтобы не путать с дефисом в тонком шрифте. */
export function fmtPercent(value: number | null | undefined, digits = 1): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toLocaleString('ru-RU', { maximumFractionDigits: digits })} %`;
}

/** Тон для дельты: рост план→факт по расходам обычно «плохо», но универсально
 *  здесь только по модулю отклонения относительно порога — оставляем нейтраль
 *  для нуля, иначе info. Конкретную «хорошо/плохо» семантику знает аномалия. */
export function deltaTone(delta: number | null | undefined): BadgeTone {
  if (typeof delta !== 'number' || delta === 0) return 'neutral';
  return 'info';
}

/** Метка периода `YYYY-MM-DD` → `Май 2026`. Обработка привязана к месяцу. */
export function fmtPeriod(period: string): string {
  const d = new Date(period);
  if (Number.isNaN(d.getTime())) return period;
  const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Короткая метка периода для оси графика: `YYYY-MM-DD` → `05.26`. */
export function fmtPeriodShort(period: string): string {
  const d = new Date(period);
  if (Number.isNaN(d.getTime())) return period;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${mm}.${yy}`;
}

/**
 * RBAC-хук агента `econ`. Зеркалит `useAgentPermission` из `@entities/user`,
 * но не расширяет его узкий union `AgentCode` (econ туда не входит) — читаем
 * `/auth/me` напрямую. Безопасный дефолт: пока права не загрузились, оба
 * флага false (лучше задизейблить кнопку, чем словить 403). См. матрицу
 * ролей на backend.
 */
export function useEconPermission(): { can_read: boolean; can_write: boolean } {
  const { data } = useMeQuery();
  const p = data?.agents.find((a) => a.agent_code === 'econ');
  return { can_read: !!p?.can_read, can_write: !!p?.can_write };
}

/** Узкая выборка полей обработки для построения сводки на дашборде. */
export interface RunSummaryPoint {
  period: string;
  module: string;
}

/** Группировка обработок по периоду для графика динамики (по возрастанию даты). */
export function sortRunsByPeriodAsc(runs: EconRun[]): EconRun[] {
  return [...runs].sort((a, b) => a.period.localeCompare(b.period));
}

/**
 * Подобрать тип документа по имени файла (`filenameHints`). Возвращает
 * `docType` первого типа, чьи хинты совпали, иначе `null` — это сигнал «не
 * угадали, нужен явный выбор пользователя». Сознательно НЕ возвращаем
 * «первый по списку» дефолт: вводящий в заблуждение тип хуже пустого выбора —
 * пользователь не заметит подмену и получит неверную классификацию.
 */
export function guessDocTypeByFilename(
  filename: string,
  docTypes: readonly EconDocType[],
): string | null {
  for (const dt of docTypes) {
    if (dt.filenameHints?.some((re) => re.test(filename))) return dt.docType;
  }
  return null;
}

/** Допускает ли тип несколько файлов в одной обработке (КС-2/КС-3 — да). */
export function isMultipleAllowed(docType: string, docTypes: readonly EconDocType[]): boolean {
  return docTypes.find((d) => d.docType === docType)?.multiple ?? false;
}

/** Извлечённый из документов период книги КС: диапазон `from..to` (ISO-даты
 *  `YYYY-MM-DD`) и/или нормализованный месяц `YYYY-MM`. Любое поле может быть
 *  `null` — источники разнородны (структурный парс vs LLM-эвристика). */
export interface ExtractedPeriod {
  from: string | null;
  to: string | null;
  /** Месяцы (`YYYY-MM`), реально встретившиеся в извлечённых данных. */
  months: string[];
}

/** Достать `YYYY-MM` из произвольной даты/строки периода. `2026-04-17` → `2026-04`,
 *  `2026-04` → `2026-04`. Возвращает `null`, если месяц не распознан. */
function monthKeyOf(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const m = value.trim().match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : null;
}

/** Прочитать строковое поле из JSON-объекта (payload/summary — `unknown`). */
function readStringField(obj: unknown, key: string): string | null {
  if (typeof obj !== 'object' || obj === null) return null;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Свести извлечённый из КС период по карточке обработки. Источники (в порядке
 * приоритета достоверности):
 *  1. `run.summary.ks_period_from` / `ks_period_to` — структурный период книги
 *     КС из парсера (если backend его прокидывает; пока зависимость, см. отчёт WS5);
 *  2. `payload.period` извлечённых КС-строк (`act_ks2`/`summary_ks3`) — LLM-эвристика
 *     формата `YYYY-MM`, доступна уже сейчас.
 * Если ничего не извлечено — `null` (нечего показывать/сверять).
 */
export function extractedKsPeriod(
  summary: unknown,
  rows: readonly EconExtractedRow[],
): ExtractedPeriod | null {
  const from = readStringField(summary, 'ks_period_from');
  const to = readStringField(summary, 'ks_period_to');

  const months = new Set<string>();
  for (const mk of [monthKeyOf(from), monthKeyOf(to)]) if (mk) months.add(mk);
  for (const row of rows) {
    const mk = monthKeyOf(readStringField(row.payload, 'period'));
    if (mk) months.add(mk);
  }

  if (!from && !to && months.size === 0) return null;
  return { from, to, months: Array.from(months).sort() };
}

/**
 * Расходится ли период обработки (`run.period`, `YYYY-MM-DD`) с периодом,
 * извлечённым из документов. Расхождение = ни один извлечённый месяц не
 * совпал с месяцем обработки (типичный кейс: КС за апрель-июнь, обработка за
 * январь). Если месяцев из документов нет — расхождения не утверждаем (`false`).
 */
export function periodMismatch(runPeriod: string, extracted: ExtractedPeriod | null): boolean {
  if (!extracted || extracted.months.length === 0) return false;
  const runMonth = monthKeyOf(runPeriod);
  if (!runMonth) return false;
  return !extracted.months.includes(runMonth);
}

/** `YYYY-MM` → `Апрель 2026` для человекочитаемого вывода извлечённого периода. */
export function fmtMonthKey(monthKey: string): string {
  const d = new Date(`${monthKey}-01`);
  if (Number.isNaN(d.getTime())) return monthKey;
  const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Человекочитаемая метка извлечённого периода: диапазон дат, если он есть
 *  (`17.04.2026 — 30.06.2026`), иначе перечень месяцев (`Апрель 2026, Май 2026`). */
export function fmtExtractedPeriod(extracted: ExtractedPeriod): string {
  if (extracted.from || extracted.to) {
    const from = extracted.from ? fmtIsoDate(extracted.from) : '…';
    const to = extracted.to ? fmtIsoDate(extracted.to) : '…';
    return `${from} — ${to}`;
  }
  return extracted.months.map(fmtMonthKey).join(', ');
}

/** `2026-04-17` → `17.04.2026`. Невалидное — как есть. */
function fmtIsoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Сообщение ошибки из тела ответа API (`{"error":{"code","message"}}`).
 * Backend раздела econ отдаёт внятные русские причины (409 «отчёт ещё не
 * сформирован», «у модуля нет активного шаблона…», «period должен быть в
 * формате YYYY-MM»), которые нельзя глотать общей фразой — показываем как есть.
 * Возвращает `null`, если тело не в этом формате (вызывающий подставляет свой
 * дефолт). Используется и при создании обработки (ModuleRunsView), и при
 * утверждении/экспорте (RunDetail) — поэтому живёт в entities, а не в странице.
 */
export function apiErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const e = (data as { error?: unknown }).error;
  if (typeof e !== 'object' || e === null || !('message' in e)) return null;
  const m = (e as { message?: unknown }).message;
  return typeof m === 'string' && m.trim() ? m : null;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Причина провала фоновой сборки отчёта XLSX (после «Утвердить»).
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Прочитать причину провала сборки отчёта из `run.summary.report_error`.
 * Backend кладёт сюда текст причины (по-русски), когда фоновая сборка XLSX
 * после утверждения упала (например «у активного шаблона модуля bdr пустая
 * карта привязки (sheet_contract)…»). Раньше эта причина была видна только в
 * серверных логах — пользователь видел «утверждено», но отчёт не собирался.
 * Поле очищается backend'ом при успешной пересборке. Читаем мягко (`unknown` →
 * строка), как `ks_period_from`: отсутствует/пусто → `null` (ошибки нет).
 */
export function reportError(summary: unknown): string | null {
  return readStringField(summary, 'report_error');
}

/* ───────────────────────────────────────────────────────────────────────────
 * Договоры с выручкой, не привязанные к карте шаблона (R3). По таким договорам
 * выручка посчитана, но в активном шаблоне модуля нет строки-привязки — в
 * собранном XLSX по ним выйдет 0. Backend кладёт перечень в
 * `run.summary.unmapped_revenue_contracts` (+ `unmapped_revenue_count` для
 * обратной совместимости) и параллельно заводит аномалию обработки
 * `kind="revenue_contract_unmapped"`, severity `major`. Это специфичный
 * actionable-кейс «почини карту шаблона», поэтому показываем отдельным понятным
 * баннером, а не только строкой в общем списке аномалий.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Одна позиция перечня непривязанных к шаблону договоров с выручкой. `metric`
 *  — по какой форме посчитана выручка (`revenue_ks2` | `revenue_ks3`); читаем
 *  как строку, новые коды не ломают вывод (через [`labelMetric`]). */
export interface UnmappedRevenueContract {
  contract: string;
  revenue: number;
  metric: string;
}

/** Прочитать число из JSON-объекта (summary/payload — `unknown`). Нечисло/NaN
 *  → `null`. */
function readNumberField(obj: unknown, key: string): number | null {
  if (typeof obj !== 'object' || obj === null) return null;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Достать перечень непривязанных к шаблону договоров с выручкой из
 * `run.summary.unmapped_revenue_contracts`. Читаем мягко (`summary: unknown`):
 * не массив → `[]`; внутри пропускаем элементы без строкового `contract` или
 * без числового `revenue` (повреждённый/частичный элемент не должен ронять
 * рендер). `metric` нормализуем к строке, отсутствует → пустая строка (подпись
 * формы тогда не показываем). Пустой/отсутствующий ключ → `[]` — баннер не
 * показывается (старые обработки / нет проблемы).
 */
export function unmappedRevenueContracts(summary: unknown): UnmappedRevenueContract[] {
  if (typeof summary !== 'object' || summary === null) return [];
  const raw = (summary as Record<string, unknown>).unmapped_revenue_contracts;
  if (!Array.isArray(raw)) return [];
  const out: UnmappedRevenueContract[] = [];
  for (const item of raw) {
    const contract = readStringField(item, 'contract');
    const revenue = readNumberField(item, 'revenue');
    if (contract === null || revenue === null) continue;
    out.push({ contract, revenue, metric: readStringField(item, 'metric') ?? '' });
  }
  return out;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Комментарии к существенным отклонениям план/факт (Р-13). Backend отбирает
 * факты с |отклонением| ≥ порога (10%) и просит LLM написать деловой
 * комментарий к каждому, складывая блок в
 * `run.summary.deviation_comments = { prompt_version, threshold_pct,
 *  comments: [{ metric, object, comment }] }`. Показываем эти пояснения в
 * карточке обработки — иначе сгенерированный (и оплаченный) LLM-текст скрыт.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Один комментарий к отклонению: метрика, объект и текст делового пояснения. */
export interface DeviationComment {
  metric: string;
  object: string;
  comment: string;
}

/**
 * Достать комментарии к отклонениям из `run.summary.deviation_comments.comments`.
 * Читаем мягко (`summary: unknown`): нет блока / не массив → `[]`; внутри
 * пропускаем элементы без непустого `comment` (частичный элемент не должен
 * ронять рендер). `metric`/`object` нормализуем к строке (пустая → не
 * показываем подпись). Пустой список → секция не выводится.
 */
export function deviationComments(summary: unknown): DeviationComment[] {
  if (typeof summary !== 'object' || summary === null) return [];
  const block = (summary as Record<string, unknown>).deviation_comments;
  if (typeof block !== 'object' || block === null) return [];
  const raw = (block as Record<string, unknown>).comments;
  if (!Array.isArray(raw)) return [];
  const out: DeviationComment[] = [];
  for (const item of raw) {
    const comment = readStringField(item, 'comment');
    if (comment === null || comment.trim() === '') continue;
    out.push({
      metric: readStringField(item, 'metric') ?? '',
      object: readStringField(item, 'object') ?? '',
      comment,
    });
  }
  return out;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Аннотации неполноты у производных метрик (ТЗ п.5.6). Обработка с одним КС-2
 * (только доход, без затрат и КС-3) даёт совпадающие суммы у «Маржа»,
 * «Доход всего» и «Выполнено СМР по КС-2». Арифметика верна (нет затрат →
 * маржа = доходу; нет КС-3 → доход всего = КС-2), но без пояснения это читается
 * как «данные выявлены неправильно». Добавляем вторичное примечание под
 * значением — но только когда УВЕРЕНЫ (нет фактов/источников соответствующего
 * типа), чтобы не зашумлять полные обработки.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Затратные метрики БДР (наличие хотя бы одной → затраты в обработке есть). */
const COST_METRICS: ReadonlySet<string> = new Set(['cost_total', 'cost_by_article']);

/** Сумма факта значима (есть ненулевое значение). `null`/0 → нет. */
function factHasValue(fact: EconCalcFact): boolean {
  return typeof fact.value_fact === 'number' && fact.value_fact !== 0;
}

/**
 * Нет ли в обработке затрат вовсе. Уверенно «нет затрат», когда среди фактов
 * группы НЕТ ни одной затратной метрики (`cost_total`/`cost_by_article`), либо
 * единственный `cost_total` равен 0 и нет ни одного `cost_by_article`-факта.
 * При таком условии `margin == revenue` не ошибка, а следствие отсутствия затрат.
 */
function hasNoCosts(facts: readonly EconCalcFact[]): boolean {
  const costFacts = facts.filter((f) => COST_METRICS.has(f.metric));
  if (costFacts.length === 0) return true;
  // Есть только нулевой cost_total без детализации по статьям → затрат нет.
  const hasByArticle = costFacts.some((f) => f.metric === 'cost_by_article');
  const anyCostValue = costFacts.some(factHasValue);
  return !hasByArticle && !anyCostValue;
}

/**
 * Примечание-неполнота для метрики «Маржа» (`margin`). Когда в фактах группы
 * нет ни одной затратной метрики (или cost_total = 0 без статей затрат) —
 * маржа численно равна доходу: поясняем это, чтобы совпадение сумм не читалось
 * как ошибка. Иначе `null` (затраты есть — примечание не нужно).
 */
export function marginNote(facts: readonly EconCalcFact[]): string | null {
  return hasNoCosts(facts) ? 'затраты не загружались — маржа равна доходу' : null;
}

/** Есть ли источник заданного `doc_type` среди источников обработки. */
function hasSourceDocType(sources: readonly EconSource[], docType: string): boolean {
  return sources.some((s) => s.doc_type === docType);
}

/** Есть ли в фактах группы метрика с ненулевым значением. */
function hasMetricValue(facts: readonly EconCalcFact[], metric: string): boolean {
  return facts.some((f) => f.metric === metric && factHasValue(f));
}

/**
 * Примечание-неполнота для метрики «Доход всего (КС-2 + КС-3)» (`revenue_total`).
 * Доход всего = КС-2 + КС-3. Если одна из составляющих отсутствует в обработке,
 * итог численно равен другой — поясняем это:
 *  - нет КС-3 (нет факта `revenue_ks3` со значением И нет источника `summary_ks3`)
 *    → «КС-3 в обработке нет — доход равен сумме КС-2»;
 *  - симметрично для отсутствия КС-2 (`revenue_ks2` / источник `act_ks2`).
 * Если отсутствуют обе или присутствуют обе — `null` (нечего пояснять).
 * Уверенность двойная (нет факта И нет источника), чтобы не зашумлять обработки,
 * где составляющая реально нулевая, но документ загружен.
 */
export function revenueTotalNote(
  facts: readonly EconCalcFact[],
  sources: readonly EconSource[],
): string | null {
  const hasKs2 = hasMetricValue(facts, 'revenue_ks2') || hasSourceDocType(sources, 'act_ks2');
  const hasKs3 = hasMetricValue(facts, 'revenue_ks3') || hasSourceDocType(sources, 'summary_ks3');
  if (hasKs2 && !hasKs3) return 'КС-3 в обработке нет — доход равен сумме КС-2';
  if (hasKs3 && !hasKs2) return 'КС-2 в обработке нет — доход равен сумме КС-3';
  return null;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Полнота обработки БДР (презентационный гейт утверждения). Внешний аудит:
 * при сбое распознавания части источников (упал doc-processor на части файлов)
 * карточка показывала «БДР готово» и активную кнопку «Утвердить», хотя доход —
 * огрызок (вошли не все договоры/объекты). Это опасно: экономист утвердит
 * неполный отчёт как полный.
 *
 * Backend УЖЕ блокирует утверждение (POST /runs/{id}/decision, decision=accepted
 * → 409) при неразобранных critical-аномалиях (включая «Источник КС не
 * распознан»). Здесь — зеркало этого гейта на фронте, плюс мягкая пометка
 * «с пробелами» для НЕкритических неполнот (skipped/failed_parse_windows),
 * чтобы пользователь видел причину ДО клика, а не ловил 409 после.
 *
 * Сигналы полноты приходят в `run.summary` (jsonb): `has_blocking_anomaly`
 * (bool), `has_gaps` (bool), `failed_ks_sources` (number), `skipped_sources`,
 * `failed_parse_windows`. Читаем мягко (`unknown` → typed): отсутствие ключа
 * (старые обработки) трактуем как «сигнала нет», не как «полнота нарушена».
 * ──────────────────────────────────────────────────────────────────────────── */

/** Прочитать булево поле из JSON-объекта (summary — `unknown`). Не-boolean
 *  (включая отсутствие ключа) → `false`. */
function readBoolField(obj: unknown, key: string): boolean {
  if (typeof obj !== 'object' || obj === null) return false;
  return (obj as Record<string, unknown>)[key] === true;
}

/**
 * Степень полноты обработки БДР:
 *  - `'complete'`  — данные полные, можно показывать «готово» и утверждать;
 *  - `'gaps'`      — НЕкритические пробелы (пропущены источники / окна парсинга):
 *                    итог неполон, но утверждение НЕ запрещено (это не critical);
 *  - `'blocking'`  — критическая неполнота (не распознан источник КС / есть
 *                    неразобранные critical-аномалии): утверждение запрещено
 *                    (backend вернёт 409), отчёт собирать нельзя.
 */
export type RunCompleteness = 'complete' | 'gaps' | 'blocking';

/** Сводка полноты обработки: степень + разобранные на части причины (для
 *  подписи под бейджем). Считается из summary прогона и списка аномалий —
 *  единый источник истины для бейджа статуса, пометки дохода и гейта кнопки. */
export interface RunCompletenessInfo {
  level: RunCompleteness;
  /** Число неразобранных critical-аномалий (зеркало backend-гейта). */
  unresolvedCritical: number;
  /** Число источников КС, которые не удалось распознать (`failed_ks_sources`). */
  failedKsSources: number;
  /** Есть ли НЕкритические пробелы (`has_gaps`: skipped/failed_parse_windows). */
  hasGaps: boolean;
}

/**
 * Вычислить полноту обработки по summary прогона и аномалиям.
 *
 * Блокирующая неполнота (`'blocking'`) — если выполнено ЛЮБОЕ:
 *  - есть неразобранные critical-аномалии ([`unresolvedCriticalCount`] > 0);
 *  - `summary.has_blocking_anomaly === true`;
 *  - `summary.failed_ks_sources > 0` (не распознан источник КС → доход огрызок).
 * Это ровно тот гейт, что и на backend (409 при accepted) — кнопку утверждения
 * гасим по нему же.
 *
 * Иначе, если есть НЕкритические пробелы (`summary.has_gaps === true`) — `'gaps'`
 * (мягкое предупреждение, утверждение разрешено). Иначе — `'complete'`.
 */
export function runCompleteness(
  summary: unknown,
  anomalies: readonly EconAnomaly[],
): RunCompletenessInfo {
  const unresolvedCritical = unresolvedCriticalCount(anomalies);
  const failedKsSources = readNumberField(summary, 'failed_ks_sources') ?? 0;
  const hasBlockingAnomaly = readBoolField(summary, 'has_blocking_anomaly');
  const hasGaps = readBoolField(summary, 'has_gaps');

  const blocking = unresolvedCritical > 0 || hasBlockingAnomaly || failedKsSources > 0;
  const level: RunCompleteness = blocking ? 'blocking' : hasGaps ? 'gaps' : 'complete';

  return { level, unresolvedCritical, failedKsSources, hasGaps };
}

/** Тон бейджа статуса полноты: blocking → danger, gaps → warning,
 *  complete → success (как обычный статус «готово»). */
export function completenessTone(level: RunCompleteness): BadgeTone {
  switch (level) {
    case 'blocking':
      return 'danger';
    case 'gaps':
      return 'warning';
    default:
      return 'success';
  }
}

/** Короткая подпись бейджа статуса полноты (вместо «готово» при неполноте). */
export function completenessLabel(level: RunCompleteness): string {
  switch (level) {
    case 'blocking':
      return 'БДР неполный — требует разбора';
    case 'gaps':
      return 'БДР с пробелами';
    default:
      return 'БДР готово';
  }
}

/**
 * Развёрнутое пояснение под бейджем статуса полноты (почему неполный/с
 * пробелами). Собирается из конкретных причин, чтобы экономист понимал, что
 * именно не вошло в итог. `null` для `'complete'` (пояснение не нужно).
 */
export function completenessExplanation(info: RunCompletenessInfo): string | null {
  if (info.level === 'complete') return null;
  const reasons: string[] = [];
  if (info.failedKsSources > 0) {
    reasons.push(
      info.failedKsSources === 1
        ? 'не распознан источник КС'
        : `не распознаны источники КС (${info.failedKsSources})`,
    );
  }
  if (info.unresolvedCritical > 0) {
    reasons.push(`критические аномалии без разбора (${info.unresolvedCritical})`);
  }
  if (info.level === 'blocking') {
    const base =
      reasons.length > 0
        ? reasons.join('; ')
        : 'есть критическая неполнота данных';
    return `${base} — итог неполный, часть источников не вошла. Утверждение запрещено, пока причины не разобраны.`;
  }
  // gaps: некритические пропуски.
  return 'часть источников или фрагментов документов не распозналась — итог может быть неполным. Утверждение разрешено, но проверьте состав документов.';
}

/** Заведомо неполон ли денежный итог обработки (доход/выполнено) — для пометки
 *  «часть источников не вошла» рядом с итоговыми суммами. Истинно при любой
 *  неполноте (blocking ИЛИ gaps): в обоих случаях итог может быть огрызком. */
export function revenueLikelyIncomplete(level: RunCompleteness): boolean {
  return level !== 'complete';
}
